/**
 * Per-model Vertex location routing — the pure rule shared by every Vertex
 * client in the app (`services/geminiClient.ts` server facade + browser proxy,
 * `services/dam/damAi.ts` DAM build client).
 *
 * Kept in its own side-effect-free module so the DAM client can use it without
 * importing `geminiClient.ts` (which constructs the SDK singleton and patches
 * `window.fetch` at module load).
 *
 * Vertex AI availability is SPLIT across locations (verified by direct
 * `:generateContent` probes, project datacrate-btm-b5edd, 2026-09-29):
 *
 *   model                          global   us-central1
 *   gemini-3.1-pro-preview          200      404
 *   gemini-3.8/3.7/3.6/3.5-flash    200      404
 *   gemini-3.5-flash-lite           200      404
 *   gemini-2.5-pro / 2.5-flash      200      200
 *
 * Every gemini-3.x family (Pro, Flash, Flash-Lite) is pinned to `global`.
 * gemini-4-argon also selects `global`; its documented us/eu endpoints are
 * not selected by this application. Other models (2.5-*, embeddings and
 * unknown IDs) route to the
 * configured base location. The 2.5 line now also answers at `global`, but it
 * deliberately stays on the regional base so today's traffic is unchanged.
 *
 * History: until 2026-09-29 the rule matched only the 3.x `-pro` line, so a
 * 3.x flash id routed to the regional base and 404'd (see the warning that
 * used to live in `config/geminiModels.ts`).
 *
 * REVISIT when the 3.x line reaches regional GA: once it is published in
 * `us-central1`, this rule can be dropped and a single base location will
 * serve every stage again.
 */

/**
 * Models this application routes to global. The historical export name is
 * retained because bounded retry/deadline policies also consume it. Argon is
 * exact: this does not assume endpoint support for future Gemini 4 models.
 * Argon availability: https://docs.cloud.google.com/gemini-enterprise-agent-platform/models/gemini/4-argon
 */
export const GLOBAL_ONLY_MODEL = /^(?:gemini-3(?:\.\d+)?-|gemini-4-argon$)/i;

/** `global` for an explicitly globally routed model, otherwise `baseLocation`. */
export function vertexLocationForModel(model: string, baseLocation: string): string {
  return GLOBAL_ONLY_MODEL.test(model) ? 'global' : baseLocation;
}
